import { useState } from 'react'
import { FileText, Loader2 } from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import {
  useAgreementStatus,
  useAcceptAgreement,
  useLegalDocument,
  legalDocumentPath,
  type RequiredAgreement,
} from '@/api/legal-hooks'
import { GateHelp } from './GateHelp'

/**
 * Click-through terms acceptance gate. Renders its children unless the
 * backend says a required legal document is unaccepted, in which case it
 * blocks with the document (the canonical server-rendered page, embedded)
 * and an explicit "I agree" action.
 *
 * The backend may require MORE than one document (e.g. a US clinic owner
 * accepts the MSA and the Business Associate Agreement). Documents are
 * presented sequentially: accepting one refetches the status, the next
 * unaccepted document takes its place, and the gate clears after the last.
 *
 * Fails open: while the status is loading or if the check errors, children
 * render normally — a flaky check must never lock practitioners out of
 * clinical software. The acceptance record is what matters legally, and
 * that is only ever written by the explicit accept action.
 */
export function AgreementGate({ children }: { children: React.ReactNode }) {
  const { data } = useAgreementStatus()

  const pendingDocs = data?.pending
    ? data.required.filter((d) => d.accepted_at === null)
    : []
  const pendingDoc = pendingDocs[0]

  if (!pendingDoc || !data) return <>{children}</>

  // key resets per-document state (the checkbox) when the next doc loads.
  return (
    <AgreementScreen
      key={pendingDoc.uuid}
      doc={pendingDoc}
      docNumber={data.required.length - pendingDocs.length + 1}
      docCount={data.required.length}
    />
  )
}

// The primary agreements incorporate the Privacy Policy + AUP by reference;
// standalone addenda (BAA, DPA) are agreed to on their own text.
const INCORPORATING_DOC_TYPES = new Set(['msa', 'practitioner_terms', 'user_terms'])

// The only documents with a public page (`@slugs` in claro-elixir's
// LegalPageController). Linking any other doc_type to `/legal/<slug>` 404s,
// so the AUP is read here, in the gate, instead (CLA-1845).
const PUBLIC_DOC_TYPES = new Set(['privacy_policy', 'practitioner_terms', 'subprocessor_list'])

function AgreementScreen({
  doc,
  docNumber,
  docCount,
}: {
  doc: RequiredAgreement
  docNumber: number
  docCount: number
}) {
  const accept = useAcceptAgreement()
  // A document the agreement incorporates (the AUP), opened in place of it.
  const [reading, setReading] = useState<string | null>(null)
  const shownType = reading ?? doc.doc_type
  const { data: document, isLoading, isError, refetch } = useLegalDocument(shownType)
  const [agreed, setAgreed] = useState(false)

  return (
    <div className="flex flex-col h-dvh w-full bg-surface">
      <div className="flex-shrink-0 border-b border-border-subtle px-6 py-4">
        <div className="max-w-3xl mx-auto flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-claro-50 flex items-center justify-center">
            <FileText className="w-4.5 h-4.5 text-claro-600" />
          </div>
          <div>
            <h1 className="text-base font-semibold text-ink">{doc.title}</h1>
            <p className="text-xs text-ink-secondary">
              {docCount > 1 ? `Document ${docNumber} of ${docCount} — ` : ''}
              Version {doc.version} — please review and accept to continue using Claro
            </p>
          </div>
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4">
        {reading && (
          <div className="max-w-3xl mx-auto mb-2">
            <button
              onClick={() => setReading(null)}
              className="text-sm text-claro-600 hover:underline"
            >
              Back to the {doc.title}
            </button>
          </div>
        )}
        <div className="max-w-3xl mx-auto rounded-xl border border-border-subtle bg-white px-6 py-5">
          {isLoading ? (
            <div className="flex items-center justify-center py-12 text-ink-secondary">
              <Loader2 className="w-5 h-5 animate-spin" />
            </div>
          ) : isError || !document ? (
            PUBLIC_DOC_TYPES.has(shownType) ? (
              <p className="text-sm text-ink-secondary">
                We couldn't load the document here.{' '}
                <a
                  href={legalDocumentPath(shownType)}
                  target="_blank"
                  rel="noreferrer"
                  className="link"
                >
                  Open it in a new tab
                </a>{' '}
                to review, then return here to accept.
              </p>
            ) : (
              <p className="text-sm text-ink-secondary">
                We couldn't load the document.{' '}
                <button onClick={() => refetch()} className="text-claro-600 hover:underline">
                  Try again
                </button>
              </p>
            )
          ) : (
            <div className="legal-content">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{document.content}</ReactMarkdown>
            </div>
          )}
        </div>
      </div>

      <div className="flex-shrink-0 border-t border-border-subtle px-6 py-4">
        <div className="max-w-3xl mx-auto flex flex-col sm:flex-row sm:items-center gap-3">
          <label className="flex items-start gap-2.5 flex-1 cursor-pointer">
            <input
              type="checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              className="mt-0.5 w-4 h-4 rounded border-border-subtle text-claro-600 focus:ring-claro-500"
            />
            <span className="text-sm text-ink-secondary leading-snug">
              {INCORPORATING_DOC_TYPES.has(doc.doc_type) ? (
                <>
                  I have read and agree to the {doc.title}, including the documents it
                  incorporates by reference (
                  <a
                    href="/privacy"
                    target="_blank"
                    rel="noreferrer"
                    className="link"
                  >
                    Privacy Policy
                  </a>
                  {' and '}
                  <button
                    type="button"
                    onClick={(e) => {
                      // Inside the label: don't tick the box.
                      e.preventDefault()
                      setReading('acceptable_use_policy')
                    }}
                    className="text-claro-600 hover:underline"
                  >
                    Acceptable Use Policy
                  </button>
                  ).
                </>
              ) : (
                <>I have read and agree to the {doc.title}.</>
              )}
            </span>
          </label>
          <div className="flex flex-col items-stretch sm:items-end gap-1">
            <button
              onClick={() => accept.mutate(doc.uuid)}
              disabled={!agreed || accept.isPending}
              className="inline-flex items-center justify-center gap-2 px-5 py-2.5 text-sm font-medium text-white bg-claro-600 rounded-lg hover:bg-claro-700 disabled:bg-primary-disabled disabled:text-on-primary-disabled disabled:cursor-not-allowed transition-colors"
            >
              {accept.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
              I agree
            </button>
            {accept.isError && (
              <p className="text-xs text-on-error-soft">
                Something went wrong recording your acceptance — please try again.
              </p>
            )}
          </div>
        </div>
        {/* CLA-1390: the shell's feedback widget is behind this gate. */}
        <div className="max-w-3xl mx-auto mt-3">
          <GateHelp gate="agreement" />
        </div>
      </div>
    </div>
  )
}
